/**
 * Auto-Scroll Slider Component
 * Automatically cycles through images at a specified interval with looping
 */

if (!customElements.get('auto-scroll-slider')) {
  customElements.define(
    'auto-scroll-slider',
    class AutoScrollSlider extends HTMLElement {
      constructor() {
        super();
        
        this.currentIndex = 0;
        this.slides = [];
        this.progressBars = [];
        this.interval = parseInt(this.dataset.interval) || 2500;
        this.autoplayTimer = null;
        this.isTransitioning = false;
        
        this.init();
      }
      
      init() {
        // Get all slides and progress bars
        this.slides = Array.from(this.querySelectorAll('.auto-scroll-slider__slide'));
        this.progressBars = Array.from(this.querySelectorAll('.auto-scroll-progress-bar__fill'));
        
        if (this.slides.length <= 1) {
          // No need for auto-scroll if only one slide
          return;
        }
        
        // Set CSS variable for progress duration
        this.style.setProperty('--progress-duration', `${this.interval}ms`);
        
        // Play media on the first active slide
        const firstActiveSlide = this.slides.find(slide => slide.classList.contains('active'));
        if (firstActiveSlide) {
          this.playSlideMedia(firstActiveSlide);
        }
        
        // Start the first progress bar
        this.startProgressBar(0);
        
        // Start autoplay
        this.startAutoplay();
        
        // Pause on hover (desktop) - also pause progress bar
        this.addEventListener('mouseenter', () => {
          this.pauseAutoplay();
          this.pauseProgressBar();
        });
        this.addEventListener('mouseleave', () => {
          this.startAutoplay();
          this.resumeProgressBar();
        });
        
        // Pause when page is not visible
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) {
            this.pauseAutoplay();
            this.pauseProgressBar();
          } else {
            this.startAutoplay();
            this.resumeProgressBar();
          }
        });
        
        // Pause when modal is opened (for Shopify product modals)
        this.setupModalListeners();
      }
      
      setupModalListeners() {
        // Listen for modal opens from within this slider
        const modalOpeners = this.querySelectorAll('modal-opener');
        modalOpeners.forEach(opener => {
          opener.addEventListener('click', () => {
            this.pauseAutoplay();
            this.pauseProgressBar();
          });
        });
        
        // Resume when modal closes - listen for modal-close events
        document.addEventListener('modalClosed', () => {
          // Only resume if not hovering
          if (!this.matches(':hover')) {
            this.startAutoplay();
            this.resumeProgressBar();
          }
        });
      }
      
      startAutoplay() {
        // Clear any existing timer
        this.pauseAutoplay();
        
        // Don't start if page is hidden
        if (document.hidden) return;
        
        // Set new timer
        this.autoplayTimer = setInterval(() => {
          this.nextSlide();
        }, this.interval);
      }
      
      pauseAutoplay() {
        if (this.autoplayTimer) {
          clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }
      
      nextSlide() {
        if (this.isTransitioning || this.slides.length <= 1) return;
        
        this.isTransitioning = true;
        
        // Calculate next index (with looping)
        const nextIndex = (this.currentIndex + 1) % this.slides.length;
        
        // If looping back to start, reset all progress bars
        if (nextIndex === 0) {
          this.resetAllProgressBars();
        }
        
        this.goToSlide(nextIndex);
      }
      
      resetAllProgressBars() {
        this.progressBars.forEach(bar => {
          bar.classList.remove('active', 'completed', 'paused');
          bar.style.width = '0%';
        });
      }
      
      goToSlide(index) {
        if (index === this.currentIndex || !this.slides[index]) {
          this.isTransitioning = false;
          return;
        }
        
        // Complete the previous progress bar
        if (this.progressBars[this.currentIndex]) {
          this.progressBars[this.currentIndex].classList.remove('active', 'paused');
          this.progressBars[this.currentIndex].classList.add('completed');
        }
        
        // Pause any videos in current slide
        if (this.slides[this.currentIndex]) {
          this.pauseSlideMedia(this.slides[this.currentIndex]);
          this.slides[this.currentIndex].classList.remove('active');
        }
        
        // Add active class to new slide
        this.slides[index].classList.add('active');
        
        // Play media in new slide
        this.playSlideMedia(this.slides[index]);
        
        // Update current index
        this.currentIndex = index;
        
        // Start the new progress bar
        this.startProgressBar(index);
        
        // Allow next transition after animation completes
        setTimeout(() => {
          this.isTransitioning = false;
        }, 500); // Match CSS transition duration
      }
      
      startProgressBar(index) {
        if (!this.progressBars[index]) return;
        
        // Reset and start the progress bar
        this.progressBars[index].classList.remove('completed', 'paused');
        this.progressBars[index].style.width = '0%';
        
        // Force reflow to restart animation
        void this.progressBars[index].offsetWidth;
        
        this.progressBars[index].classList.add('active');
      }
      
      pauseProgressBar() {
        if (this.progressBars[this.currentIndex]) {
          this.progressBars[this.currentIndex].classList.add('paused');
        }
      }
      
      resumeProgressBar() {
        if (this.progressBars[this.currentIndex]) {
          this.progressBars[this.currentIndex].classList.remove('paused');
        }
      }
      
      playSlideMedia(slide) {
        if (!slide) return;
        
        const mediaType = slide.dataset.mediaType;
        
        // Handle video/external_video
        if (mediaType === 'video' || mediaType === 'external_video') {
          const deferredMedia = slide.querySelector('deferred-media');
          if (deferredMedia) {
            // Auto-load and play the video
            deferredMedia.loadContent(false);
            
            // Wait a bit for content to load, then play
            setTimeout(() => {
              const video = slide.querySelector('video');
              if (video) {
                // Mute video for autoplay to work
                video.muted = true;
                video.play().catch(() => {
                  // Autoplay failed, user interaction required
                  console.log('Video autoplay prevented by browser');
                });
              }
            }, 200);
          }
        }
      }
      
      pauseSlideMedia(slide) {
        if (!slide) return;
        
        // Pause any videos
        const videos = slide.querySelectorAll('video');
        videos.forEach(video => {
          if (!video.paused) {
            video.pause();
          }
        });
        
        // For external videos (YouTube, Vimeo), we'd need to use their APIs
        // This is a basic implementation
        const iframes = slide.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          // Pause by reloading src (basic approach)
          const src = iframe.src;
          if (src) {
            iframe.src = '';
            iframe.src = src.replace('autoplay=1', 'autoplay=0');
          }
        });
      }
      
      disconnectedCallback() {
        // Clean up timer when element is removed
        this.pauseAutoplay();
      }
    }
  );
}

// Helper: Dispatch modal closed event for other components
// This listens for modal dialog closes and dispatches a custom event
document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType === 1 && (node.classList?.contains('modal') || node.tagName === 'MODAL-DIALOG')) {
          document.dispatchEvent(new CustomEvent('modalClosed'));
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});

